import React, { useState } from 'react';
import {
  Button,
  TextInput,
  Select,
  SelectItem,
  Card,
  Title,
  Badge,
  List,
  ListItem,
} from '@tremor/react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

import { MeetingAttachment } from '../../types/meeting';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meetComm: string;
  meetDate: string;
  attachments: MeetingAttachment[];
  onAttachmentsChange: () => void;
}

const attachmentTypes = [
  'Minutes',
  'Agenda',
  'Presentation',
  'Report',
  'Other'
] as const;

export default function MeetingAttachmentsModal({
  isOpen,
  onClose,
  meetComm,
  meetDate,
  attachments,
  onAttachmentsChange
}: Props) {
  const { user } = useAuth();
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newType, setNewType] = useState<string>(attachmentTypes[0]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateUrl(newUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error: insertError } = await supabase
        .from('comm_meeting_attachments')
        .insert({
          meet_comm: meetComm,
          meet_date: meetDate,
          title: newTitle,
          url: newUrl,
          attachment_type: newType,
          created_by: user?.id
        });

      if (insertError) throw insertError;

      setNewTitle('');
      setNewUrl('');
      setNewType(attachmentTypes[0]);
      onAttachmentsChange();
    } catch (err) {
      setError('Failed to add attachment. Please try again.');
      console.error('Error adding attachment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('comm_meeting_attachments')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      onAttachmentsChange();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment. Please try again.');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Minutes':
        return 'blue';
      case 'Agenda':
        return 'green';
      case 'Presentation':
        return 'yellow';
      case 'Report':
        return 'purple';
      default:
        return 'gray';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <div className="flex justify-between items-center mb-6">
          <Title>Meeting Attachments</Title>
          <Button variant="light" onClick={onClose}>Close</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <TextInput
            placeholder="Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            required
          />
          <TextInput
            placeholder="URL"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            required
          />
          <Select
            value={newType}
            onValueChange={setNewType}
            placeholder="Select type"
          >
            {attachmentTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </Select>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          >
            Add Attachment
          </Button>
        </form>

        <List>
          {attachments.map((attachment) => (
            <ListItem key={attachment.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Badge color={getTypeColor(attachment.attachment_type)}>
                  {attachment.attachment_type}
                </Badge>
                <div>
                  <p className="font-medium">{attachment.title}</p>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline text-sm"
                  >
                    View Attachment
                  </a>
                </div>
              </div>
              <Button
                variant="light"
                color="red"
                onClick={() => handleDelete(attachment.id)}
                size="xs"
              >
                Delete
              </Button>
            </ListItem>
          ))}
        </List>
      </Card>
    </div>
  );
}
